/**
 * The part of the page tree that main.tsx (browser) and entry-server.tsx
 * (prerender) share, inside their routers. Hydration needs both to render the
 * same tree: not only the same HTML, but the same component shape, because
 * useId (BrandLogo's SVG gradients) derives its ids from it.
 */
import { ReactNode, Suspense, lazy, useSyncExternalStore } from "react";
import { LanguageProvider } from "./contexts/LanguageContext";
import { OverlayProvider } from "./store/overlayStore";
import { RouteMeta } from "./seo/RouteMeta";
import { MetaPixelPageViews } from "./components/MetaPixelPageViews";

const Toaster = lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));

/**
 * Sonner draws its own container, which the prerendered HTML does not have;
 * mounting it after the first render keeps hydration an exact match. It also
 * loads on demand: marketing pages never toast.
 */
const noSubscribe = () => () => {};

function ClientToaster() {
  // false on the server and while hydrating, true in the browser after that.
  const inBrowser = useSyncExternalStore(noSubscribe, () => true, () => false);
  if (!inBrowser) return null;
  return (
    <Suspense fallback={null}>
      <Toaster
        richColors
        position="top-right"
        closeButton
        toastOptions={{
          duration: 4000,
          className: "text-sm"
        }}
      />
    </Suspense>
  );
}

export function Site({ children }: { children: ReactNode }) {
  return (
    <LanguageProvider>
      <OverlayProvider>
        <RouteMeta />
        {/* Renders nothing; effects only, so the prerender matches. */}
        <MetaPixelPageViews />
        {children}
        <ClientToaster />
      </OverlayProvider>
    </LanguageProvider>
  );
}
